/**
 * Create Modal
 * Modal for creating Rituals, Coaching Sessions, or Communities
 * Uses shadcn Dialog pattern
 */

import { CoachingIcon, CommunityCreateIcon, RitualsIcon } from "@/components/icons"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { useTranslation } from "@/language"

interface CreateModalProps {
  isOpen: boolean
  onClose: () => void
  onNavigate?: (path: string) => void
}

interface CreateOption {
  id: string
  icon: React.ReactNode
  title: string
  description: string
  action: () => void
}

const buttonColors: Record<string, { bg: string; iconBg: string; iconColor: string }> = {
  ritual: { bg: "#C9A8761A", iconBg: "#C9A87626", iconColor: "#C9A876" },
  coaching: { bg: "#B8B8961A", iconBg: "#B8B89626", iconColor: "#B8B896" },
  community: { bg: "#C867571A", iconBg: "#C8675726", iconColor: "#C86757" },
}

export function CreateModal({ isOpen, onClose, onNavigate }: CreateModalProps) {
  const { t } = useTranslation("common")

  const createOptions: CreateOption[] = [
    {
      id: "ritual",
      icon: <RitualsIcon />,
      title: t("createModal.ritual"),
      description: t("createModal.ritualDescription"),
      action: () => {
        onClose()
        onNavigate?.("/rituals/create")
      },
    },
    {
      id: "coaching",
      icon: <CoachingIcon />,
      title: t("createModal.coaching"),
      description: t("createModal.coachingDescription"),
      action: () => {
        onClose()
        onNavigate?.("/coaching-sessions/create")
      },
    },
    {
      id: "community",
      icon: <CommunityCreateIcon />,
      title: t("createModal.community"),
      description: t("createModal.communityDescription"),
      action: () => {
        onClose()
        onNavigate?.("/communities/create")
      },
    },
  ]

  return (
    <Dialog open={isOpen} onOpenChange={(open: boolean) => !open && onClose()}>
      <DialogContent className="md:h-[524px] md:w-[680px]" overlayClass="bg-[rgb(0,0,0,0.9)]">
        <DialogHeader className="text-center">
          <DialogTitle className="text-charcoal text-15px md:text-[24px] font-semibold text-center mt-7 !mb-0">
            {t("createModal.title")}
          </DialogTitle>
          <DialogDescription className="text-stone text-center text-sm leading-relaxed px-5 sm:px-15 md:px-30 mt-4">
            {t("createModal.subtitle")}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4 mt-3 px-1">
          {createOptions.map(option => {
            const colors = buttonColors[option.id]
            return (
              <div
                key={option.id}
                className="flex items-center justify-center gap-4 min-h-[95px] md:h-[95px] px-4 text-left rounded-xl border cursor-pointer"
                style={{ backgroundColor: colors.bg, borderColor: colors.bg }}
                onClick={option.action}
              >
                <div
                  className="w-15 h-15 rounded-full flex items-center justify-center flex-shrink-0 p-0"
                  style={{ backgroundColor: colors.iconBg, color: colors.iconColor }}
                >
                  {option.icon}
                </div>
                <div className="flex-1">
                  <span className="block font-semibold text-charcoal text-[16px]">
                    {option.title}
                  </span>
                  <span className="block text-[14px] text-stone mt-0.5">{option.description}</span>
                </div>
              </div>
            )
          })}
        </div>
      </DialogContent>
    </Dialog>
  )
}
